// src/components/fleet/RenewalWarningModal.jsx
import { AlertTriangle, X, CheckCircle, Clock, XCircle } from "lucide-react";

export function RenewalWarningModal({ document, isOpen, onClose, onContinue }) {
  if (!isOpen || !document) return null;

  // Calcular días hasta vencimiento
  const getDaysUntilExpiration = () => {
    if (!document.expiration_date) return null;
    const today = new Date();
    const expiration = new Date(document.expiration_date);
    const diffTime = expiration - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysUntil = getDaysUntilExpiration();

  // Determinar nivel de advertencia
  const getWarningLevel = () => {
    if (daysUntil === null) return "info";
    if (daysUntil < 0) return "expired"; // VENCIDO
    if (daysUntil <= 7) return "urgent"; // URGENTE
    if (daysUntil <= 30) return "warning"; // POR VENCER
    return "normal"; // VIGENTE
  };

  const warningLevel = getWarningLevel();

  // Configuración según el nivel
  const warningConfig = {
    expired: {
      icon: XCircle,
      bgColor: "bg-red-50",
      borderColor: "border-red-500",
      iconColor: "text-red-600",
      titleColor: "text-red-900",
      textColor: "text-red-800",
      title: "⚠️ Documento Vencido",
      message: `Este documento venció hace ${Math.abs(daysUntil)} días.`,
      consequences: [
        "Puede generar multas y sanciones administrativas",
        "El vehículo/conductor no puede operar legalmente",
        "Posibles costos adicionales de regularización",
        "Tiempo de procesamiento puede ser mayor",
      ],
      buttonText: "Continuar con Renovación Extemporánea",
      buttonColor: "bg-red-600 hover:bg-red-700",
    },
    urgent: {
      icon: AlertTriangle,
      bgColor: "bg-orange-50",
      borderColor: "border-orange-500",
      iconColor: "text-orange-600",
      titleColor: "text-orange-900",
      textColor: "text-orange-800",
      title: "🔔 Renovación Urgente",
      message: `El documento vence en ${daysUntil} día${daysUntil === 1 ? "" : "s"}.`,
      consequences: [
        "Es importante renovar cuanto antes",
        "Evita vencimiento que podría generar multas",
        "Asegura continuidad operativa sin interrupciones",
      ],
      buttonText: "Continuar con Renovación",
      buttonColor: "bg-orange-600 hover:bg-orange-700",
    },
    warning: {
      icon: Clock,
      bgColor: "bg-yellow-50",
      borderColor: "border-yellow-500",
      iconColor: "text-yellow-600",
      titleColor: "text-yellow-900",
      textColor: "text-yellow-800",
      title: "📅 Renovación Próxima",
      message: `El documento vence en ${daysUntil} días.`,
      consequences: [
        "Buen momento para iniciar la renovación",
        "Tiempo suficiente para completar trámites",
        "Evita apuros de último momento",
      ],
      buttonText: "Continuar con Renovación",
      buttonColor: "bg-yellow-600 hover:bg-yellow-700",
    },
    normal: {
      icon: CheckCircle,
      bgColor: "bg-blue-50",
      borderColor: "border-blue-500",
      iconColor: "text-blue-600",
      titleColor: "text-blue-900",
      textColor: "text-blue-800",
      title: "✅ Renovación Anticipada",
      message: `El documento es válido por ${daysUntil} días más.`,
      consequences: [
        "Renovación anticipada permite planificar mejor",
        "Asegura continuidad sin riesgo de vencimiento",
        "Evita contratiempos futuros",
      ],
      buttonText: "Continuar con Renovación",
      buttonColor: "bg-blue-600 hover:bg-blue-700",
    },
    info: {
      icon: CheckCircle,
      bgColor: "bg-gray-50",
      borderColor: "border-gray-500",
      iconColor: "text-gray-600",
      titleColor: "text-gray-900",
      textColor: "text-gray-800",
      title: "Renovar Documento",
      message: "Sin fecha de vencimiento registrada.",
      consequences: [
        "Verifica las fechas al renovar",
        "Asegura completar todos los campos requeridos",
      ],
      buttonText: "Continuar con Renovación",
      buttonColor: "bg-gray-600 hover:bg-gray-700",
    },
  };

  const config = warningConfig[warningLevel];
  const Icon = config.icon;

  // Información del documento
  const documentInfo = {
    tipo: document.document_type?.name || "Sin tipo",
    entidad:
      document.documentable?.placa ||
      `${document.documentable?.nombre || ""} ${document.documentable?.apellidos || ""}`.trim() ||
      document.documentable?.nombre_comercial ||
      "Sin información",
    emision: document.issue_date
      ? new Date(document.issue_date).toLocaleDateString("es-PE")
      : "-",
    vencimiento: document.expiration_date
      ? new Date(document.expiration_date).toLocaleDateString("es-PE")
      : "-",
  };

  return (
    <>
      {/* Backdrop */}
      <div
        role="button"
        tabIndex={0}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]"
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onClose();
        }}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg animate-slideUp">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <h2 className="text-xl font-bold text-gray-900">
              Confirmar Renovación
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Información del Documento */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Tipo:</span>
                <span className="text-sm font-medium text-gray-900">
                  {documentInfo.tipo}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Entidad:</span>
                <span className="text-sm font-medium text-gray-900">
                  {documentInfo.entidad}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Emisión:</span>
                <span className="text-sm font-medium text-gray-900">
                  {documentInfo.emision}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Vencimiento:</span>
                <span
                  className={`text-sm font-bold ${warningLevel === "expired"
                      ? "text-red-600"
                      : warningLevel === "urgent"
                        ? "text-orange-600"
                        : "text-gray-900"
                    }`}
                >
                  {documentInfo.vencimiento}
                </span>
              </div>
            </div>

            {/* Advertencia Principal */}
            <div
              className={`${config.bgColor} border-l-4 ${config.borderColor} rounded-lg p-4`}
            >
              <div className="flex items-start">
                <Icon
                  className={`h-6 w-6 ${config.iconColor} mt-0.5 mr-3 flex-shrink-0`}
                />
                <div className="flex-1">
                  <h3 className={`text-sm font-bold ${config.titleColor} mb-2`}>
                    {config.title}
                  </h3>
                  <p className={`text-sm ${config.textColor} mb-3`}>
                    {config.message}
                  </p>

                  {config.consequences.length > 0 && (
                    <div className="space-y-1">
                      <p
                        className={`text-xs font-semibold ${config.textColor}`}
                      >
                        Consideraciones:
                      </p>
                      <ul
                        className={`text-xs ${config.textColor} space-y-1 ml-4`}
                      >
                        {config.consequences.map((item, index) => (
                          <li key={index} className="flex items-start">
                            <span className="mr-2">•</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Nota Informativa */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-xs text-blue-800">
                <strong>ℹ️ Proceso de renovación:</strong> El documento actual
                se marcará como "reemplazado" y se creará un nuevo documento
                vigente. Se generarán alertas automáticas según las nuevas
                fechas configuradas.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
            <button
              onClick={onClose}
              className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 font-medium transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={onContinue}
              className={`px-5 py-2.5 text-white rounded-lg font-medium transition-all shadow-md hover:shadow-lg ${config.buttonColor}`}
            >
              {config.buttonText}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
